import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { GetParam, CreateParam, UpdateParam } from "../ParamIO/api"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { showNotification } from "aldoo-ra/Notification"
import { SwatchIcon, TrashIcon } from "@heroicons/react/24/outline"

const PARAM_ADDRESS = "SITE_THEME_USER_AVAILABLE"

export default function UserAvailableThemes() {
  const [allThemes, setAllThemes] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => { setIsClient(true) }, [])

  useEffect(() => {
    if (!isClient) return
    load()
  }, [isClient])

  async function load() {
    setLoading(true)
    try {
      const themesRes = await GetCMSContent({
        match: { "content.type": "SiteTheme", deleted: { $ne: true } },
      })
      const themes = (themesRes?.data || []).map((t) => ({
        value: t._id,
        name: t.content.name,
      }))
      setAllThemes(themes)

      const param = await GetParam(PARAM_ADDRESS).catch(() => null)
      if (param?.address && param?.value) {
        const ids = Array.isArray(param.value) ? param.value : JSON.parse(param.value)
        setSelectedIds(ids)
      }
    } catch (err) {
      console.error("UserAvailableThemes load error:", err)
    } finally {
      setLoading(false)
    }
  }

  async function save(ids) {
    try {
      const paramData = { address: PARAM_ADDRESS, value: ids, description: "Theme IDs available to users in theme switcher" }
      const existing = await GetParam(PARAM_ADDRESS).catch(() => null)
      if (existing?.address) {
        await UpdateParam(paramData)
      } else {
        await CreateParam(paramData)
      }
      showNotification({ message: "User available themes updated", className: "bg-admin_success text-admin_text_inv" })
    } catch (err) {
      showNotification({ message: "Failed to save user available themes", className: "bg-admin_error text-admin_text_inv" })
    }
  }

  function handleAdd(selected) {
    if (!selected.length) return
    const id = selected[0].value
    if (selectedIds.includes(id)) return
    const next = [...selectedIds, id]
    setSelectedIds(next)
    save(next)
  }

  function handleRemove(id) {
    const next = selectedIds.filter((x) => x !== id)
    setSelectedIds(next)
    save(next)
  }

  if (!isClient) return null

  if (loading) {
    return (
      <div className="bg-paper rounded-lg border border-admin_bordered p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-admin_neutral_2 rounded w-64 mb-4"></div>
          <div className="h-4 bg-admin_neutral_2 rounded w-full mb-2"></div>
        </div>
      </div>
    )
  }

  const selectedThemes = selectedIds.map((id) => allThemes.find((t) => t.value === id)).filter(Boolean)
  const unselectedThemes = allThemes.filter((t) => !selectedIds.includes(t.value))

  return (
    <div className="bg-paper rounded-lg border border-admin_bordered p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <SwatchIcon className="w-6 h-6 text-admin_primary" />
        <Typography component="h1" variant="admin_h4" className="text-admin_text">
          User Available Themes
        </Typography>
      </div>

      <Typography variant="admin_body_dim" className="mb-6 mt-0">
        Themes users can choose from in the theme switcher widget on the site. Add themes to this list to make them visible to users.
      </Typography>

      {/* Add theme dropdown */}
      {unselectedThemes.length > 0 && (
        <div className="mb-4">
          <Typography variant="admin_label" className="block mb-2">
            Add theme
          </Typography>
          <DropdownSelect
            variant="admin"
            value={[]}
            onChange={handleAdd}
            options={unselectedThemes}
            placeholder="Select a theme to add..."
            multiSelection={false}
            showCheckbox={false}
            showSelectionTick={false}
            enableResetButton={false}
            searchEnabled={true}
            className="w-full max-w-sm"
          />
        </div>
      )}

      {/* Selected themes list */}
      <div className="flex flex-col gap-2">
        {selectedThemes.length === 0 && (
          <Typography variant="admin_txt1_dim" className="italic">
            No themes added yet.
          </Typography>
        )}
        {selectedThemes.map((theme) => (
          <div
            key={theme.value}
            className="flex items-center justify-between p-3 bg-admin_paper rounded-lg border border-admin_bordered"
          >
            <Typography variant="admin_label" className="text-admin_text">
              {theme.name}
            </Typography>
            <button
              onClick={() => handleRemove(theme.value)}
              className="p-1.5 rounded text-admin_text_dim hover:text-admin_error hover:bg-admin_error/10 transition"
              aria-label={`Remove ${theme.name}`}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
